<script>
  import { onMount } from 'svelte';
  import { ohlcvData, selectedSymbol } from '../../lib/store.js';
  
  let canvas;
  let ctx;
  let chartContainer;
  let containerWidth = 800;
  let containerHeight = 400;
  
  // Default to 1D, removed timeframe selector
  let chartType = 'candle'; // candle, line
  
  function resizeCanvas() {
    if (chartContainer) {
      containerWidth = chartContainer.offsetWidth;
      containerHeight = chartContainer.offsetHeight - 50; // Account for controls
      if (containerHeight < 150) containerHeight = 150; // Minimum height
      if (containerHeight > window.innerHeight * 0.3) {
        containerHeight = Math.floor(window.innerHeight * 0.3); // Max 30% of viewport (reduced from 40%)
      }
    }
  }
  
  function drawChart() {
    if (!ctx || !$ohlcvData || $ohlcvData.length === 0) return;
    
    ctx.clearRect(0, 0, containerWidth, containerHeight);
    
    // Set background
    ctx.fillStyle = '#0a0b0d';
    ctx.fillRect(0, 0, containerWidth, containerHeight);
    
    const padding = { top: 20, right: 60, bottom: 30, left: 10 };
    const chartWidth = containerWidth - padding.left - padding.right;
    const chartHeight = containerHeight - padding.top - padding.bottom;
    
    // Find min and max prices
    let minPrice = Infinity;
    let maxPrice = -Infinity;
    
    $ohlcvData.forEach(candle => {
      minPrice = Math.min(minPrice, candle.low || 0);
      maxPrice = Math.max(maxPrice, candle.high || 0);
    });
    
    const priceRange = maxPrice - minPrice;
    const priceScale = chartHeight / priceRange;
    
    // Draw grid lines
    ctx.strokeStyle = '#1e1f22';
    ctx.lineWidth = 1;
    
    for (let i = 0; i <= 5; i++) {
      const y = padding.top + (chartHeight * i / 5);
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(containerWidth - padding.right, y);
      ctx.stroke();
      
      // Draw price labels
      const price = maxPrice - (priceRange * i / 5);
      ctx.fillStyle = '#8a8b8d';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(price.toFixed(2), containerWidth - 5, y + 3);
    }
    
    // Draw candles or line
    const candleWidth = Math.max(1, (chartWidth / $ohlcvData.length) * 0.8);
    const candleSpacing = chartWidth / $ohlcvData.length;
    
    if (chartType === 'candle') {
      $ohlcvData.forEach((candle, index) => {
        const x = padding.left + (index * candleSpacing) + (candleSpacing / 2);
        const openY = padding.top + ((maxPrice - candle.open) * priceScale);
        const closeY = padding.top + ((maxPrice - candle.close) * priceScale);
        const highY = padding.top + ((maxPrice - candle.high) * priceScale);
        const lowY = padding.top + ((maxPrice - candle.low) * priceScale);
        
        const isGreen = candle.close >= candle.open;
        
        // Draw wick
        ctx.strokeStyle = isGreen ? '#00ff88' : '#ff3b3b';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, highY);
        ctx.lineTo(x, lowY);
        ctx.stroke();
        
        // Draw body
        ctx.fillStyle = isGreen ? '#00ff88' : '#ff3b3b';
        const bodyHeight = Math.abs(closeY - openY) || 1;
        const bodyY = Math.min(openY, closeY);
        
        ctx.fillRect(x - candleWidth/2, bodyY, candleWidth, bodyHeight);
      });
    } else {
      // Draw line chart
      ctx.strokeStyle = '#00d4ff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      
      $ohlcvData.forEach((candle, index) => {
        const x = padding.left + (index * candleSpacing) + (candleSpacing / 2);
        const y = padding.top + ((maxPrice - candle.close) * priceScale);
        
        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      
      ctx.stroke();
    }
    
    // Draw time labels
    ctx.fillStyle = '#8a8b8d';
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'center';
    
    const labelInterval = Math.ceil($ohlcvData.length / 6);
    $ohlcvData.forEach((candle, index) => {
      if (index % labelInterval === 0) {
        const x = padding.left + (index * candleSpacing) + (candleSpacing / 2);
        const date = new Date(candle.time);
        const timeStr = date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });
        ctx.fillText(timeStr, x, containerHeight - 5);
      }
    });
  }
  
  // Redraw only when the actual inputs change: chart data, chart type, or
  // canvas dimensions. Referencing them makes this reactive block depend on
  // them explicitly. (Do NOT redraw on every update via afterUpdate — under
  // Svelte 5 that creates an update -> draw -> update feedback loop because
  // resizeCanvas() writes containerWidth/Height, which the canvas binds to,
  // which triggers another update. That loop pegs the CPU = "browser busy".)
  $: if (ctx && canvas && $ohlcvData && $ohlcvData.length > 0 && chartType && containerWidth && containerHeight) {
    drawChart();
  }

  onMount(() => {
    ctx = canvas.getContext('2d');
    resizeCanvas();
    drawChart();

    const handleResize = () => {
      resizeCanvas();
      drawChart();
    };

    window.addEventListener('resize', handleResize);

    // Use Intersection Observer to detect when component becomes visible
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && ctx && canvas) {
          // Component is visible, ensure proper sizing and redraw
          setTimeout(() => {
            resizeCanvas();
            drawChart();
          }, 50);
        }
      });
    }, { threshold: 0.1 });

    if (chartContainer) {
      observer.observe(chartContainer);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  });

</script>

<div class="chart-container" bind:this={chartContainer}>
  <div class="chart-controls">
    <div class="chart-title">
      {$selectedSymbol} - Daily Chart
    </div>
    
    <div class="controls">
      <div class="chart-type-selector">
        <button 
          class="type-btn {chartType === 'candle' ? 'active' : ''}"
          on:click={() => chartType = 'candle'}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect x="7" y="2" width="2" height="12" fill="currentColor"/>
            <rect x="5" y="5" width="6" height="6" fill="currentColor" opacity="0.6"/>
          </svg>
          Candles
        </button>
        <button 
          class="type-btn {chartType === 'line' ? 'active' : ''}"
          on:click={() => chartType = 'line'}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2 12L6 8L10 10L14 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Line
        </button>
      </div>
    </div>
  </div>
  
  <canvas 
    bind:this={canvas}
    width={containerWidth}
    height={containerHeight}
  ></canvas>
</div>

<style>
  @import './CandlestickChart.css';
</style>